import type { FC } from "react";
import { useEffect, useRef } from "react";

import { LoadMore } from "@ui/components/LoadMore";

import { Loader } from "@jti/core-components/components/Loader";
import { useMessageBus } from "@jti/fe-message-bus";
import { type SearchData } from "@jti/rest-api-types";

import type { TranslationsResults } from "../../types";
import { SearchCard } from "../SearchCard";

import { useSearch } from "./useSearch";

import styles from "./SearchResults.module.scss";

export type SearchResultsProps = {
  translations: TranslationsResults;
  searchType?: string;
  locale?: string;
  loadResultsPerPage?: number;
  initialResultsNumber?: number;
};

export const SearchResults: FC<SearchResultsProps> = ({
  translations,
  searchType,
  locale,
  loadResultsPerPage,
  initialResultsNumber,
}) => {
  const { isLoading, searchText, searchResponse, loadMoreHandler } = useSearch({
    type: searchType,
    locale,
    loadResultsPerPage,
    initialResultsNumber,
  });
  const messageBus = useMessageBus();
  const messageBusRef = useRef(messageBus);
  messageBusRef.current = messageBus;

  const isShowLoadMoreButton =
    !isLoading && searchResponse?.data?.length > 0 && searchResponse?.data?.length < (searchResponse?.meta?.total ?? 0);
  const loadMoreNumber = (searchResponse?.meta?.total ?? 0) - (searchResponse?.meta?.to ?? 0);
  const searchData = searchResponse?.data ?? [];

  useEffect(() => {
    if (searchText) {
      const isFaqSearch = searchType === "faq";
      messageBusRef.current.publish("tracking.event", {
        payload: {
          event_name: isFaqSearch ? "faq_search" : "search",
          additionalData: {
            search_term: searchText,
            ...(isFaqSearch && { section: "search-results", template_name: "search-results" }),
          },
        },
      });
    }
  }, [searchText, searchType]);

  if (!searchText) {
    return null;
  }

  return (
    <div className={styles.searchContainer}>
      {isLoading && searchData.length === 0 && (
        <div className={styles.mainLoader}>
          <Loader />
        </div>
      )}
      <div className={styles.searchResultsContainer}>
        {searchData.map((card: SearchData) => (
          <SearchCard
            key={card.item_id}
            img={card.thumbnail}
            title={card.title}
            description={card.content.body}
            linkLabel={translations.readMoreLabel}
            link={card.url}
            searchType={searchType}
            searchValue={searchText}
          />
        ))}
      </div>
      {searchData.length === 0 && !isLoading && (
        <div className={styles.noResults} data-testid="searchNoResults">
          {translations.noResultsLabel}
        </div>
      )}
      {isShowLoadMoreButton && (
        <LoadMore
          onClick={loadMoreHandler}
          data-testid="loadMoreButton"
          className={styles.loadMore}
          label={translations.loadMoreLabel?.replace(
            "{0}",
            String(loadMoreNumber >= (loadResultsPerPage ?? 0) ? loadResultsPerPage : loadMoreNumber)
          )}
        />
      )}
      {isLoading && searchData.length > 0 && (
        <div className={styles.loadMoreLoaderContainer}>
          <Loader />
        </div>
      )}
    </div>
  );
};
